import { useCallback, Dispatch, SetStateAction, useMemo } from 'react';
import { GameState, Player, TransferPlayer, League } from '../../../domain/entities';
import { getPlayerTeam, isPlayerRecruitmentBlocked } from '../../../services/GameService';
import { MAX_SQUAD_SIZE, getPlayerValue, isPlayerInTransferCooldown, calculateReleasePenalty } from '../../../domain/constants';
import { AIBiddingService } from '../../../services/AIBiddingService';
import { FreeAgentService } from '../../../services/FreeAgentService';

type SetGameState = Dispatch<SetStateAction<GameState | { gameStarted: false; }>>;

interface UseTransferManagementProps {
    gameState: GameState | { gameStarted: false };
    setGameState: SetGameState;
}

const addPlayerToSquad = (state: GameState, player: TransferPlayer, cost: number, wage: number): GameState => {
    const newState = JSON.parse(JSON.stringify(state));
    (newState as any).gameStarted = true;
    
    const transferListIndex = newState.playersForSale.indexOf(player.id);
    if (transferListIndex > -1) {
        newState.playersForSale.splice(transferListIndex, 1);
    }

    const newPlayerTeam = getPlayerTeam(newState);
    const newPlayerBase = JSON.parse(JSON.stringify(player));

    // Reset morale if negative (Unhappy or Furious), keep if positive or neutral
    const shouldResetMorale = player.morale === 'Unhappy' || player.morale === 'Furious';
    const morale = shouldResetMorale ? 'Happy' : player.morale;
    const moraleReason = shouldResetMorale ? 'Excited about a new challenge' : player.moraleReason;

    const newPlayer: Player = {
        ...newPlayerBase,
        wage,
        lastSeasonChange: 0,
        gamesPlayed: 0,
        goalsScored: 0,
        goalsConceded: 0,
        injury: null,
        history: [...(newPlayerBase.history || [])],
        morale,
        moraleReason,
        potmAwards: 0,
        isListed: false,
        listedDate: undefined,
        askingPrice: undefined,
    };
    newPlayer.history.push({ season: newState.season, teamName: newPlayerTeam.name, skill: player.skill, gamesPlayed: 0, goalsScored: 0, goalsConceded: 0, assists: 0, yellowCards: 0, redCards: 0, potmAwards: 0, });
    newPlayerTeam.squad.push(newPlayer);
    newPlayerTeam.finances.balance -= cost;
    newPlayerTeam.finances.history.push({ season: newState.season, week: newState.currentWeek, event: `Bought ${player.name}`, amount: -cost });
    if (cost > newPlayerTeam.clubHistory.recordTransferFee.fee) {
        newPlayerTeam.clubHistory.recordTransferFee = { playerName: player.name, fee: cost, season: newState.season, type: 'in' };
    }
    if (cost > newPlayerTeam.clubHistory.recordTransferIn.fee) {
        newPlayerTeam.clubHistory.recordTransferIn = { playerName: player.name, fee: cost, season: newState.season };
    }
    newState.transferHistory.push({
        season: newState.season, week: newState.currentWeek, playerName: player.name, playerSkill: player.skill, fromTeam: player.originalTeamName, toTeam: newPlayerTeam.name, fee: cost,
        playerId: player.id, playerAge: player.age, playerPosition: player.position, playerPotential: player.potential
    });
    const originalTeam = Object.values(newState.leagues).flatMap((l: League) => l.teams).find(t => t.name === player.originalTeamName);
    if (originalTeam) {
        // Remove player from original team and clear listing status
        const playerToRemoveIndex = originalTeam.squad.findIndex(p => p.id === player.id);
        if (playerToRemoveIndex !== -1) {
            originalTeam.squad[playerToRemoveIndex].isListed = false;
            originalTeam.squad[playerToRemoveIndex].listedDate = undefined;
            originalTeam.squad[playerToRemoveIndex].askingPrice = undefined;
        }
        originalTeam.squad = originalTeam.squad.filter(p => p.id !== player.id);
    }
    return newState;
}

export const useTransferManagement = ({ gameState, setGameState }: UseTransferManagementProps) => {
    const sellPlayer = useCallback((player: Player, priceMultiplier: number = 1.0): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const currentTeam = getPlayerTeam(newState);

                if (currentTeam.squad.length <= 16) {
                    resolve({ success: false, message: 'Cannot sell player. You must maintain a minimum squad of 16 players.' });
                    return prevState;
                }

                // Check if player was promoted from youth academy and is within 6-month lock
                if (player.isPromotedYouth && player.promotionDate) {
                    const weeksPerSeason = newState.config.WEEKS_PER_SEASON;
                    const monthsInWeeks = Math.ceil((6 / 12) * weeksPerSeason); // 6 months = half a season
                    const weeksElapsed = (newState.season - player.promotionDate.season) * weeksPerSeason + (newState.currentWeek - player.promotionDate.week);

                    if (weeksElapsed < monthsInWeeks) {
                        const weeksRemaining = monthsInWeeks - weeksElapsed;
                        resolve({ success: false, message: `Cannot sell ${player.name}. Youth players must remain in the squad for 6 months after promotion. Weeks remaining: ${weeksRemaining}.` });
                        return prevState;
                    }
                }

                const value = getPlayerValue(player, newState.playerLeagueName, newState.gameType, newState.config);
                // Calculate asking price with multiplier - adjust buyer probability based on price
                const baseAskingPrice = Math.round(value * priceMultiplier);
                // Probability of finding buyer varies: lower prices (0.9x) = 50-80%, normal (1.0x) = 20-50%, higher prices (1.1x) = 10-40%
                const buyerProbabilityModifier = priceMultiplier <= 0.95 ? 1.5 : priceMultiplier >= 1.05 ? 0.5 : 1.0;
                const salePrice = Math.round(baseAskingPrice * (0.8 + Math.random() * 0.3 * buyerProbabilityModifier));

                // Find potential buyers - only teams in same league and with sufficient funds
                const potentialBuyers = Object.values(newState.leagues)
                    .flatMap(l => l.teams)
                    .filter(t =>
                        t.name !== currentTeam.name &&
                        t.finances.balance > salePrice &&
                        t.squad.length < MAX_SQUAD_SIZE &&
                        t.finances.balance > 0 // Must have positive balance
                    );

                // Filter to only teams that actually need this position (higher quality match)
                const qualityBuyers = potentialBuyers.filter(t => {
                    const teamPlayerAvgSkill = t.squad.length > 0 ? t.squad.reduce((sum, p) => sum + p.skill, 0) / t.squad.length : 0;
                    return player.skill > (teamPlayerAvgSkill - 5); // Only interested if player is comparable to their squad
                });

                // Remove player from lineup
                const lineup = currentTeam.tactics.lineup;
                for (const pos in lineup) {
                    if (lineup[pos as any] === player.id) {
                        lineup[pos as any] = null;
                    }
                }

                if (qualityBuyers.length === 0) {
                    // No buyer found - list the player for transfer instead of failing
                    const playerIndex = currentTeam.squad.findIndex((p: Player) => p.id === player.id);
                    if (playerIndex !== -1) {
                        const listedPlayer = currentTeam.squad[playerIndex];
                        listedPlayer.isListed = true;
                        listedPlayer.listedDate = { season: newState.season, week: newState.currentWeek };
                        listedPlayer.askingPrice = baseAskingPrice;

                        // Add player to global transfer list for off-season purchases
                        if (!newState.playersForSale.includes(player.id)) {
                            newState.playersForSale.push(player.id);
                        }

                        // Reduce morale when listed
                        const moraleMap: Record<string, number> = { 'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1 };
                        const numberToMorale: Record<number, string> = { 5: 'Ecstatic', 4: 'Happy', 3: 'Content', 2: 'Unhappy', 1: 'Furious' };
                        const currentLevel = moraleMap[listedPlayer.morale];
                        const newLevel = Math.max(1, currentLevel - 2); // Reduce by 2 levels
                        listedPlayer.morale = numberToMorale[newLevel] as any;
                        listedPlayer.moraleReason = 'Listed for transfer';
                    }

                    resolve({ success: true, message: `${player.name} has been listed for transfer at £${salePrice.toLocaleString()}. Other clubs may express interest in the coming weeks.` });
                    return newState;
                }

                const buyer = qualityBuyers[Math.floor(Math.random() * qualityBuyers.length)];

                const playerToSellIndex = currentTeam.squad.findIndex((p: Player) => p.id === player.id);
                const [playerToSell] = currentTeam.squad.splice(playerToSellIndex, 1);

                // Reset morale if negative (Unhappy or Furious), keep if positive or neutral
                const shouldResetMorale = playerToSell.morale === 'Unhappy' || playerToSell.morale === 'Furious';
                playerToSell.morale = shouldResetMorale ? 'Happy' : playerToSell.morale;
                playerToSell.moraleReason = shouldResetMorale ? 'Excited about a new challenge' : playerToSell.moraleReason;

                buyer.squad.push(playerToSell);

                currentTeam.finances.balance += salePrice;
                currentTeam.finances.history.push({ season: newState.season, week: newState.currentWeek, event: `Sold ${player.name} to ${buyer.name}`, amount: salePrice });
                buyer.finances.balance -= salePrice;

                if (salePrice > currentTeam.clubHistory.recordTransferFee.fee) {
                    currentTeam.clubHistory.recordTransferFee = { playerName: player.name, fee: salePrice, season: newState.season, type: 'out' };
                }
                if (salePrice > currentTeam.clubHistory.recordTransferOut.fee) {
                    currentTeam.clubHistory.recordTransferOut = { playerName: player.name, fee: salePrice, season: newState.season };
                }

                newState.transferHistory.push({
                    season: newState.season, week: newState.currentWeek, playerName: player.name, playerSkill: player.skill, fromTeam: currentTeam.name, toTeam: buyer.name, fee: salePrice,
                    playerId: player.id, playerAge: player.age, playerPosition: player.position, playerPotential: player.potential
                });

                resolve({ success: true, message: `${player.name} has been sold to ${buyer.name} for £${salePrice.toLocaleString()}!`});
                return newState;
            });
        });
    }, [setGameState]);

    const releasePlayer = useCallback((player: Player): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const newPlayerTeam = getPlayerTeam(newState);

                if (newPlayerTeam.squad.length <= 16) {
                    resolve({ success: false, message: 'Cannot release player. You must maintain a minimum squad of 16 players.' });
                    return prevState;
                }

                // Check if player is in transfer cooldown (cannot release within 6 months of last transfer)
                if (isPlayerInTransferCooldown(player, newState.season, newState.currentWeek)) {
                    const weeksRemaining = 30; // Simplified - exact calculation in actual logic
                    resolve({ success: false, message: `Cannot release ${player.name}. They were recently transferred and must settle for at least 6 months (${weeksRemaining} weeks) before being released.` });
                    return prevState;
                }

                // Calculate release penalty: 30% of player's market value
                const playerLeagueName = Object.entries(newState.leagues).find(([_, league]) =>
                    league.teams.some(t => t.name === newPlayerTeam.name)
                )?.[0] || '';
                const releasePenalty = calculateReleasePenalty(player, playerLeagueName, newState.gameType, newState.config);

                if (newPlayerTeam.finances.balance < releasePenalty) {
                    resolve({ success: false, message: `Not enough funds to pay the release penalty of £${releasePenalty.toLocaleString()}. (30% of player market value)` });
                    return prevState;
                }

                const lineup = newPlayerTeam.tactics.lineup;
                for (const pos in lineup) {
                    if (lineup[pos as any] === player.id) {
                        lineup[pos as any] = null;
                    }
                }

                // Remove player from squad
                newPlayerTeam.squad = newPlayerTeam.squad.filter((p: Player) => p.id !== player.id);

                // Deduct release penalty
                newPlayerTeam.finances.balance -= releasePenalty;
                newPlayerTeam.finances.history.push({ season: newState.season, week: newState.currentWeek, event: `Released ${player.name}`, amount: -releasePenalty });

                // Convert to free agent
                if (!newState.freeAgents) {
                    newState.freeAgents = [];
                }

                // Create free agent record with a deep copy of the player
                const freeAgent = {
                    playerId: player.id,
                    playerName: player.name,
                    position: player.position,
                    skill: player.skill,
                    potential: player.potential,
                    age: player.age,
                    form: player.form,
                    fitness: player.fitness,
                    wage: player.wage,
                    hue: player.hue,
                    faceEmoji: player.faceEmoji,
                    playerSnapshot: JSON.parse(JSON.stringify(player)),
                    availableSince: { season: newState.season, week: newState.currentWeek },
                    // No deadline set until first bid arrives
                    totalBids: 0,
                    offers: [] as any[],
                    userBidStatus: 'no_bid' as const,
                };

                newState.freeAgents.push(freeAgent);

                // Record transfer to free agent status
                newState.transferHistory.push({
                    season: newState.season,
                    week: newState.currentWeek,
                    playerName: player.name,
                    playerSkill: player.skill,
                    fromTeam: newPlayerTeam.name,
                    toTeam: 'Free Agent',
                    fee: 0,
                    playerId: player.id,
                    playerAge: player.age,
                    playerPosition: player.position,
                    playerPotential: player.potential,
                });

                resolve({ success: true, message: `${player.name} has been released and is now a free agent. Release penalty paid: £${releasePenalty.toLocaleString()}. Bidding will begin once the first club places an offer.` });
                return newState;
            });
        });
    }, [setGameState]);

    const { negotiatePurchase, finalizePurchase } = useMemo(() => {
        const negotiatePurchase = async (player: TransferPlayer, offerMultiplier: number): Promise<{ success: boolean, message: string; acceptedOffer?: number }> => {
            return new Promise(resolve => {
                setGameState(prevState => {
                    if (!prevState.gameStarted) { resolve({ success: false, message: 'Game not started.' }); return prevState; }
                    if (prevState.transferBanned) {
                        resolve({ success: false, message: 'The club is currently under a transfer ban.' });
                        return prevState;
                    }

                    // Check per-player club negotiation limit (max 2 bids with club per week)
                    if (!prevState.playerNegotiationsThisWeek) {
                        prevState.playerNegotiationsThisWeek = {};
                    }
                    const clubBidsThisWeek = prevState.playerNegotiationsThisWeek[`${player.id}_club`] || 0;
                    if (clubBidsThisWeek >= 2) {
                        resolve({ success: false, message: `You can only make 2 club offers for this player per week. Try again next week.` });
                        return prevState;
                    }

                    const playerTeam = getPlayerTeam(prevState);
                    if (playerTeam.squad.length >= MAX_SQUAD_SIZE) { resolve({ success: false, message: `Squad is full! Maximum size is ${MAX_SQUAD_SIZE}.` }); return prevState; }
                    const offer = Math.round(player.askingPrice * offerMultiplier);
                    if (playerTeam.finances.balance < offer) { resolve({ success: false, message: 'Not enough funds for this offer.' }); return prevState; }
                    if (player.injury) { resolve({ success: false, message: `Cannot sign an injured player.` }); return prevState; }

                    // Check if player is blocked from recruitment
                    const blockStatus = isPlayerRecruitmentBlocked(player, prevState.season, prevState.currentWeek, prevState.config);
                    if (blockStatus.blocked) {
                        resolve({ success: false, message: `${player.name} is unavailable for recruitment until week ${blockStatus.weeksRemaining} weeks from now.` });
                        return prevState;
                    }

                    const newState: GameState = JSON.parse(JSON.stringify(prevState));
                    if (!newState.playerNegotiationsThisWeek) {
                        newState.playerNegotiationsThisWeek = {};
                    }

                    const isListed = prevState.playersForSale.includes(player.id);
                    let clubSuccessChance = isListed ? (0.8 + (offerMultiplier - 1) * 1.5) : (0.2 + (offerMultiplier - 1) * 2);
                    clubSuccessChance = Math.max(0.05, Math.min(0.95, clubSuccessChance));
                    if (Math.random() > clubSuccessChance) {
                        // Club rejected - increment club counter and track rejection
                        newState.playerNegotiationsThisWeek[`${player.id}_club`] = (newState.playerNegotiationsThisWeek[`${player.id}_club`] || 0) + 1;

                        // Find the player in the transfer list and increment rejection count
                        const playerInLeagues = Object.values(newState.leagues).flatMap((league: League) => league.teams).flatMap(team => team.squad).find(p => p.id === player.id);
                        if (playerInLeagues) {
                            if (!playerInLeagues.recruitmentRejections) {
                                playerInLeagues.recruitmentRejections = { teamRejections: 0, playerRejections: 0 };
                            }
                            playerInLeagues.recruitmentRejections.teamRejections++;

                            // If 3 team rejections reached, block for 4 weeks
                            if (playerInLeagues.recruitmentRejections.teamRejections >= 3) {
                                const weeksPerSeason = newState.config.WEEKS_PER_SEASON;
                                const blockWeeks = 4;
                                let blockUntilWeek = newState.currentWeek + blockWeeks;
                                let blockUntilSeason = newState.season;

                                // Handle season overflow
                                if (blockUntilWeek > weeksPerSeason) {
                                    blockUntilSeason += Math.floor((blockUntilWeek - 1) / weeksPerSeason);
                                    blockUntilWeek = ((blockUntilWeek - 1) % weeksPerSeason) + 1;
                                }

                                playerInLeagues.recruitmentRejections.blockedUntil = { season: blockUntilSeason, week: blockUntilWeek };
                                playerInLeagues.recruitmentRejections.teamRejections = 0; // Reset rejection count when blocking
                            }
                        }

                        resolve({ success: false, message: `${player.originalTeamName} rejected your offer of £${offer.toLocaleString()}.` });
                        return newState;
                    }

                    // Club accepted - increment club counter to mark this bid attempt
                    newState.playerNegotiationsThisWeek[`${player.id}_club`] = (newState.playerNegotiationsThisWeek[`${player.id}_club`] || 0) + 1;
                    const message = isListed ? `${player.originalTeamName} accepted your offer. You can now negotiate personal terms.` : `${player.originalTeamName} surprisingly accepted your poaching offer. You can now negotiate personal terms.`;
                    resolve({ success: true, message, acceptedOffer: offer });
                    return newState;
                });
            });
        };

        const finalizePurchase = async (player: TransferPlayer, transferFee: number, wageMultiplier: number): Promise<{ success: boolean, message: string }> => {
            return new Promise(resolve => {
                setGameState(prevState => {
                    if (!prevState.gameStarted) { resolve({ success: false, message: 'Game not started.' }); return prevState; }
                    if (prevState.transferBanned) {
                        resolve({ success: false, message: 'The club is currently under a transfer ban.' });
                        return prevState;
                    }

                    // Check per-player wage negotiation limit (max 2 bids with player per week)
                    if (!prevState.playerNegotiationsThisWeek) {
                        prevState.playerNegotiationsThisWeek = {};
                    }
                    const playerBidsThisWeek = prevState.playerNegotiationsThisWeek[`${player.id}_player`] || 0;
                    if (playerBidsThisWeek >= 2) {
                        resolve({ success: false, message: `You can only make 2 wage offers to this player per week. Try again next week.` });
                        return prevState;
                    }

                    const playerTeam = getPlayerTeam(prevState);
                    if (playerTeam.finances.balance < transferFee) { resolve({ success: false, message: 'Not enough funds for the transfer fee.' }); return prevState; }

                    // Check if player is blocked from recruitment
                    const blockStatus = isPlayerRecruitmentBlocked(player, prevState.season, prevState.currentWeek, prevState.config);
                    if (blockStatus.blocked) {
                        resolve({ success: false, message: `${player.name} is unavailable for recruitment until ${blockStatus.weeksRemaining} weeks from now.` });
                        return prevState;
                    }

                    const newState: GameState = JSON.parse(JSON.stringify(prevState));
                    if (!newState.playerNegotiationsThisWeek) {
                        newState.playerNegotiationsThisWeek = {};
                    }

                    const allPlayersInLeague = newState.leagues[newState.playerLeagueName].teams.flatMap(t => t.squad);
                    const playerTeamLeagueAverageSkill = allPlayersInLeague.reduce((sum, p) => sum + p.skill, 0) / allPlayersInLeague.length;
                    const skillDifference = player.skill - playerTeamLeagueAverageSkill;
                    const playerLeagueName = Object.keys(newState.leagues).find(leagueName => newState.leagues[leagueName].teams.some(t => t.name === player.originalTeamName)) || '';
                    const playerLeagueRank = newState.config.LEAGUE_NAMES.indexOf(playerLeagueName);
                    const myLeagueRank = newState.config.LEAGUE_NAMES.indexOf(newState.playerLeagueName);
                    let baseSuccessChance = 0.5 + (wageMultiplier - 1) * 2;
                    let ambitionPenalty = 0;
                    if (skillDifference > 5 && myLeagueRank >= playerLeagueRank) { ambitionPenalty = Math.min(0.4, (skillDifference - 5) / 50); }

                    // Increment player counter - this completes the wage negotiation attempt whether player accepts or rejects
                    newState.playerNegotiationsThisWeek[`${player.id}_player`] = (newState.playerNegotiationsThisWeek[`${player.id}_player`] || 0) + 1;

                    if (Math.random() > baseSuccessChance - ambitionPenalty) {
                        // Player rejected - track rejection and possibly block player

                        // Find the player in the transfer list and increment rejection count
                        const playerInLeagues = Object.values(newState.leagues).flatMap((league: League) => league.teams).flatMap(team => team.squad).find(p => p.id === player.id);
                        if (playerInLeagues) {
                            if (!playerInLeagues.recruitmentRejections) {
                                playerInLeagues.recruitmentRejections = { teamRejections: 0, playerRejections: 0 };
                            }
                            playerInLeagues.recruitmentRejections.playerRejections++;

                            // If 3 player rejections reached, block for 4 weeks
                            if (playerInLeagues.recruitmentRejections.playerRejections >= 3) {
                                const weeksPerSeason = newState.config.WEEKS_PER_SEASON;
                                const blockWeeks = 4;
                                let blockUntilWeek = newState.currentWeek + blockWeeks;
                                let blockUntilSeason = newState.season;

                                // Handle season overflow
                                if (blockUntilWeek > weeksPerSeason) {
                                    blockUntilSeason += Math.floor((blockUntilWeek - 1) / weeksPerSeason);
                                    blockUntilWeek = ((blockUntilWeek - 1) % weeksPerSeason) + 1;
                                }

                                playerInLeagues.recruitmentRejections.blockedUntil = { season: blockUntilSeason, week: blockUntilWeek };
                                playerInLeagues.recruitmentRejections.playerRejections = 0; // Reset rejection count when blocking
                            }
                        }

                        resolve({ success: false, message: `${player.name} has rejected your contract offer. The player feels the move isn't right for their career.` });
                        return newState;
                    }

                    const newWage = Math.round(player.wage * wageMultiplier);
                    resolve({ success: true, message: `${player.name} has accepted your contract and joined the club!` });
                    return addPlayerToSquad(newState, player, transferFee, newWage);
                });
            });
        };
        return { negotiatePurchase, finalizePurchase };
    }, [setGameState]);

    const unlistPlayer = useCallback((player: Player): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const currentTeam = getPlayerTeam(newState);

                const playerIndex = currentTeam.squad.findIndex((p: Player) => p.id === player.id);
                if (playerIndex === -1 || !currentTeam.squad[playerIndex].isListed) {
                    resolve({ success: false, message: "Player is not listed for transfer." });
                    return prevState;
                }

                // Unlist the player
                currentTeam.squad[playerIndex].isListed = false;
                currentTeam.squad[playerIndex].listedDate = undefined;
                currentTeam.squad[playerIndex].askingPrice = undefined;

                // Remove player from global transfer list
                newState.playersForSale = newState.playersForSale.filter(id => id !== player.id);

                // Improve morale slightly for being kept
                const moraleMap: Record<string, number> = { 'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1 };
                const numberToMorale: Record<number, string> = { 5: 'Ecstatic', 4: 'Happy', 3: 'Content', 2: 'Unhappy', 1: 'Furious' };
                const currentLevel = moraleMap[currentTeam.squad[playerIndex].morale];
                const newLevel = Math.min(5, currentLevel + 1); // Improve by 1 level
                currentTeam.squad[playerIndex].morale = numberToMorale[newLevel] as any;
                currentTeam.squad[playerIndex].moraleReason = 'Removed from transfer list';

                resolve({ success: true, message: `${player.name} has been removed from the transfer list.` });
                return newState;
            });
        });
    }, [setGameState]);

    // Bid management functions
    const acceptIncomingBid = useCallback((bidId: string): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState = AIBiddingService.acceptBid(prevState as GameState, bidId);
                resolve({ success: true, message: "Bid accepted. Player transfer completed." });
                return newState;
            });
        });
    }, [setGameState]);

    const rejectIncomingBid = useCallback((bidId: string): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState = AIBiddingService.rejectBid(prevState as GameState, bidId);
                resolve({ success: true, message: "Bid rejected." });
                return newState;
            });
        });
    }, [setGameState]);

    const negotiateIncomingBid = useCallback((bidId: string, percentageIncrease: number): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                // Check if bid exists and has hit negotiation limit
                const bid = (prevState as GameState).incomingBids?.find(b => b.id === bidId);
                if (!bid) {
                    resolve({ success: false, message: "Bid not found." });
                    return prevState;
                }

                if (bid.negotiationRounds >= 3) {
                    resolve({ success: false, message: `You have reached the maximum of 3 negotiation rounds for this bid. Accept or reject the current offer.` });
                    return prevState;
                }

                const newState = AIBiddingService.negotiateBid(prevState as GameState, bidId, percentageIncrease);
                resolve({ success: true, message: `Counter-offer sent (+${percentageIncrease}%). Awaiting manager response.` });
                return newState;
            });
        });
    }, [setGameState]);

    const respondToIncomingBidNegotiation = useCallback((bidId: string, accept: boolean): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState = AIBiddingService.respondToNegotiation(prevState as GameState, bidId, accept);
                if (accept) {
                    resolve({ success: true, message: "Counter-offer accepted. Player transfer completed." });
                } else {
                    resolve({ success: true, message: "Counter-offer rejected." });
                }
                return newState;
            });
        });
    }, [setGameState]);

    const submitFreeAgentBid = useCallback((freeAgentId: number, wage: number): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState = FreeAgentService.submitUserBid(prevState as GameState, freeAgentId, wage);

                // Find the free agent to get details for the message
                const freeAgent = newState.freeAgents?.find(fa => fa.playerId === freeAgentId);

                if (freeAgent) {
                    const message = `Bid submitted for ${freeAgent.playerName} at £${wage.toLocaleString()}/week. Decision deadline: Season ${freeAgent.bidDeadline.season} Week ${freeAgent.bidDeadline.week}.`;
                    resolve({ success: true, message });
                } else {
                    resolve({ success: false, message: "Free agent not found." });
                }

                return newState;
            });
        });
    }, [setGameState]);

    const acceptTransferSuggestion = useCallback((suggestionId: string): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState = JSON.parse(JSON.stringify(prevState)) as GameState;
                const team = Object.values(newState.leagues)
                    .flatMap(l => l.teams)
                    .find(t => t.name === newState.playerTeamName);

                if (!team) {
                    resolve({ success: false, message: 'Team not found' });
                    return prevState;
                }

                const suggestionIndex = team.assistantManager.transferSuggestions.findIndex(s => s.id === suggestionId);
                if (suggestionIndex === -1) {
                    resolve({ success: false, message: 'Suggestion not found' });
                    return prevState;
                }

                const suggestion = team.assistantManager.transferSuggestions[suggestionIndex];

                // Mark as accepted
                suggestion.status = 'accepted';

                // Execute action based on suggestion type
                if (suggestion.action === 'sell') {
                    // List player for sale
                    const player = team.squad.find(p => p.id === suggestion.playerId);
                    if (!player) {
                        resolve({ success: false, message: 'Player not found' });
                        return prevState;
                    }

                    player.isListed = true;
                    player.listedDate = { season: newState.season, week: newState.currentWeek };
                    player.askingPrice = suggestion.suggestedPrice || 100000;
                    if (!newState.playersForSale.includes(player.id)) {
                        newState.playersForSale.push(player.id);
                    }

                    resolve({ success: true, message: `${player.name} listed for sale at £${player.askingPrice.toLocaleString()}` });
                } else {
                    // For buy/scout/free_agent actions, just mark as accepted
                    resolve({ success: true, message: `Action accepted. Please complete the transfer through the Transfers screen.` });
                }

                return newState;
            });
        });
    }, [setGameState]);

    const rejectTransferSuggestion = useCallback((suggestionId: string): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState = JSON.parse(JSON.stringify(prevState)) as GameState;
                const team = Object.values(newState.leagues)
                    .flatMap(l => l.teams)
                    .find(t => t.name === newState.playerTeamName);

                if (!team) {
                    resolve({ success: false, message: 'Team not found' });
                    return prevState;
                }

                const suggestionIndex = team.assistantManager.transferSuggestions.findIndex(s => s.id === suggestionId);
                if (suggestionIndex === -1) {
                    resolve({ success: false, message: 'Suggestion not found' });
                    return prevState;
                }

                const suggestion = team.assistantManager.transferSuggestions[suggestionIndex];
                suggestion.status = 'rejected';

                resolve({ success: true, message: `Suggestion rejected` });
                return newState;
            });
        });
    }, [setGameState]);

    const adjustListingPrice = useCallback((player: Player, newPrice: number): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }

                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const currentTeam = getPlayerTeam(newState);

                const playerIndex = currentTeam.squad.findIndex((p: Player) => p.id === player.id);
                if (playerIndex === -1 || !currentTeam.squad[playerIndex].isListed) {
                    resolve({ success: false, message: "Player is not listed for transfer." });
                    return prevState;
                }

                // Update asking price
                const oldPrice = currentTeam.squad[playerIndex].askingPrice;
                currentTeam.squad[playerIndex].askingPrice = newPrice;

                resolve({ success: true, message: `${player.name}'s asking price updated from £${oldPrice?.toLocaleString()} to £${newPrice.toLocaleString()}` });
                return newState;
            });
        });
    }, [setGameState]);

    return {
        sellPlayer,
        releasePlayer,
        negotiatePurchase,
        finalizePurchase,
        unlistPlayer,
        acceptIncomingBid,
        rejectIncomingBid,
        negotiateIncomingBid,
        respondToIncomingBidNegotiation,
        submitFreeAgentBid,
        acceptTransferSuggestion,
        rejectTransferSuggestion,
        adjustListingPrice,
    };
};
